/* eslint-disable indent */
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { sendGet, sendPost } from 'hooks/use-fetch'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import CircularProgress from '@mui/material/CircularProgress'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import { EligiblityFactorList } from 'Components/CheckList/EligiblityFactorList'
import { TextFieldWithLabel } from 'Components/TextField'
import { DatePickerWithLabel } from 'Components/DatePicker'
import { SelectWithLabel } from 'Components/SelectWithLabel'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import { Breadcrumbs, Link, Radio, RadioGroup, Select, Stack } from '@mui/material'
import { type lookup, type ChildEditValues, type eligibilityFactor, type race } from 'core/types'
import { toast } from 'react-toastify'
import { LabelWithToolTip } from 'Components/Label/Index'
import { RaceList } from 'Components/CheckList/RaceList'
import { CheckBoxWithLabel } from 'Components/CheckBox'
import { dupeSelector, duplicateSource, translateSiblingEditValues } from 'slices/duplicateChildSlice'
import { useAppSelector } from 'hooks/reduxHooks'
import { useDuplicateCheck } from 'hooks/useDuplicateCheck'

interface Relationship {
  caregiverId: number
  fullName: string
  relationshipId: number
  isSharedMember: boolean
  livesWith: boolean
  financiallyResponsible: boolean
}

export type SiblingEditModel = ChildEditValues & {
  isUnborn?: boolean
  siblingId?: number
  informationalOnly: boolean
  livesWithChild: boolean
  address?: string
  apartment?: string
  city?: string
  state?: string
  zipCode?: string
  relationships: Relationship[]
  childFullName: string
}

const genderOptions: Array<{ id: string, name: string }> = [
  { id: 'M', name: 'Male' },
  { id: 'F', name: 'Female' }
]

interface IsDirty {
  firstName: boolean
  lastName: boolean
  dateOfBirth: boolean
  dueDate: boolean
  birthGender: boolean
  races: boolean
}

export function SiblingEdit (): JSX.Element {
  const params = useParams()
  const dupeRecord = useAppSelector(dupeSelector)
  const nav = useNavigate()
  const loadedRaceList: race[] = dupeRecord?.race.split(',').map(r => {
    const newRaceVal: race = {
      codePosition: 0,
      id: 0,
      name: r
    }
    return newRaceVal
  }) ?? []
  const loadedEfList: eligibilityFactor[] = dupeRecord?.eligibilityFactors?.split(',').map(e => {
    const ef: eligibilityFactor = {
      id: 0,
      name: e,
      stateCode: ''
    }
    return ef
  }) ?? []

  const translatedDupe: SiblingEditModel | null = dupeRecord != null
    ? {
        ...dupeRecord,
        eligibilityFactorList: loadedEfList,
        childRaceList: loadedRaceList,
        dateOfBirth: dupeRecord.dateOfBirth != null ? new Date(dupeRecord.dateOfBirth) : '',
        dueDate: dupeRecord.dueDate != null ? new Date(dupeRecord.dueDate) : '',
        birthGender: dupeRecord.birthGender.charAt(0)
      } as unknown as SiblingEditModel
    : null
  const [sibling, setSibling] = useState<SiblingEditModel | null>(translatedDupe)
  const [relationships, setRelationships] = useState<lookup[]>([])
  const [originalSiblingName, setOriginalSiblingName] = useState<string>('')
  const [showBornOption, setShowBornOption] = useState(false)
  const [isDirty, setIsDirty] = useState<IsDirty>({
    firstName: false,
    lastName: false,
    dateOfBirth: false,
    birthGender: false,
    dueDate: false,
    races: false
  })

  const firstNameInvalid = !(sibling?.isUnborn ?? false) && (sibling?.firstName === '' || sibling?.firstName === undefined)
  const lastNameInvalid = sibling?.lastName === '' || sibling?.lastName === undefined
  const dateOfBirthInvalid = !(sibling?.isUnborn ?? false) && (sibling?.dateOfBirth === null || sibling?.dateOfBirth === undefined)
  const birthGenderInvalid = !(sibling?.isUnborn ?? false) && (sibling?.birthGender === undefined || sibling?.birthGender === '')
  const dueDateInvalid = (sibling?.isUnborn ?? false) && (sibling?.dueDate === null || sibling?.dueDate === undefined)
  const racesInvalid = !(sibling?.isUnborn ?? false) && sibling?.childRaceList.length === 0

  const firstNameError = isDirty.firstName && firstNameInvalid
  const lastNameError = isDirty.lastName && lastNameInvalid
  const dateOfBirthError = isDirty.dateOfBirth && dateOfBirthInvalid
  const dueDateError = isDirty.dueDate && dueDateInvalid
  const birthGenderError = isDirty.birthGender && birthGenderInvalid
  const [loaded, setLoaded] = useState(false)
  const { checkAndHandleDuplicate } = useDuplicateCheck()
  // const racesError = isDirty.races && racesInvalid

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      setShowBornOption(params.siblingId === undefined)
      if (params.siblingId !== undefined) {
        const { response, success } = await sendGet(
          `/SiblingDetails/GetEditData?siblingId=${params.siblingId ?? 0}&childId=${params.childId ?? 0}`, {})

        if (success) {
          const newVals = translatedDupe != null ? { ...response.response, ...translatedDupe } : response.response
          setSibling(newVals)
          setShowBornOption(response.response.isUnborn as boolean)
          setOriginalSiblingName(`${response.response.firstName as string} ${response.response.lastName as string}`)
        }
      } else {
        const { response, success } = await sendGet(
          `/SiblingDetails/GetChildData?childId=${params.childId ?? 0}`, {})

        success && setSibling({
          firstName: '',
          lastName: '',
          dateOfBirth: null,
          informationalOnly: false,
          livesWithChild: true,
          childRaceList: [],
          eligibilityFactorList: [],
          environmentalRiskList: [],
          otherEligibilityFactor: null,
          hasSNAPBenefits: false,
          isFosterChild: false,
          isOnCashAssistance: false,
          isOnSSI: false,
          isImmigrant: false,
          relationships: response.response.relationships,
          childFullName: response.response.fullName,
          isUnborn: false,
          language: null,
          birthGender: '',
          primaryLocationID: response.response.primaryLocationID,
          address: response.response.address,
          apartment: response.response.apartment,
          city: response.response.city,
          state: response.response.state,
          zipCode: response.response.zipCode,
          siblingId: params.childId // so we use this differently between the dupes and the normal modify. Dont send this to normal modify.
        } as unknown as SiblingEditModel)
      }
      setLoaded(true)
    }

    if (params.childId !== undefined) {
      void fetchData()
    }
  }, [params.siblingId, params.childId])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response: relations, error } = await sendGet('/Relationship/GetLookup?sort=true', {})
      if (error[0] === '') {
        setRelationships(relations)
      }
    }

    void fetchData()
  }, [])

  function bornStatusChange (event: any, value: string): void {
    if (sibling != null) {
      setSibling({ ...sibling, isUnborn: (value === 'true'), dateOfBirth: (value === 'true' ? null : new Date()) })
    }
  }

  const valueChanged = (field: string, value: any): void => {
    if (sibling != null) {
      setSibling({ ...sibling, [field]: value })
    }
  }

  const handleSave = async (): Promise<void> => {
    if (firstNameInvalid || lastNameInvalid || dateOfBirthInvalid || birthGenderInvalid || racesInvalid || dueDateInvalid) {
      setIsDirty({
        firstName: true,
        lastName: true,
        dateOfBirth: true,
        birthGender: true,
        races: true,
        dueDate: true
      })
      toast.error('Please complete all required fields')
      return
    }

    const childDupeRecord = translateSiblingEditValues(sibling, duplicateSource.Sibling)
    if (await checkAndHandleDuplicate(childDupeRecord, {
      childIdOverride: sibling.siblingId ?? 0,
      routeId: params?.childId ?? '0'
    })) return
    const actualSiblingId = parseInt(params.siblingId ?? '')
    const modifiedSibling: SiblingEditModel = { ...sibling, siblingId: actualSiblingId }
    const { success, response } = await sendPost(`/SiblingDetails/PostEditData?childId=${params.childId ?? ''}`, modifiedSibling ?? {})
    const num: number = response.response
    if (success && num !== 0) {
      nav(-1)
    } else {
      toast.error('Error saving sibling')
    }
  }
  const goBack = (): void => {
    nav(-1)
  }
  if (!loaded) { return <CircularProgress /> }
  return <>
    {params.siblingId === undefined
      ? <Typography sx={{ display: 'flex', fontWeight: 'bold', paddingBottom: 2 }} variant='h5'>Add Sibling Information</Typography>
      : <Typography sx={{ display: 'flex', fontWeight: 'bold', paddingBottom: 2 }} variant='h5'>Edit Sibling Information - {originalSiblingName}</Typography>}
    <Breadcrumbs>
      <Link sx={{ cursor: 'pointer' }} underline="hover" color="inherit" onClick={goBack} >Return to Care Network Details</Link>
    </Breadcrumbs>

    <Paper elevation={0} square={false} sx={{ padding: 2 }}>
      <Typography variant='h6' gutterBottom>Sibling Details</Typography>
      {sibling === null
        ? <CircularProgress />
        : <>
          <Grid container spacing={2}>
            {showBornOption && <Grid item xs={6}>
              <RadioGroup value={sibling.isUnborn} onChange={bornStatusChange} defaultValue={false} name='isUnborn' sx={{ paddingTop: '10px' }}>
                <FormControlLabel value={false} control={<Radio />} label='This child has been born' />
                <FormControlLabel value={true} control={<Radio />} label='Unborn child' />
              </RadioGroup>
            </Grid>
            }
            <Grid item xs={12} sm={6}>
              <TextFieldWithLabel
                required={firstNameError}
                showRequiredText={firstNameError}
                error={firstNameError}
                label='First Name'
                name='firstName'
                dataTestId='firstName'
                onChange={(e) => {
                  setSibling({ ...sibling, firstName: e.target.value })
                  !isDirty.firstName && setIsDirty({ ...isDirty, firstName: true })
                }}
                value={sibling.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldWithLabel
                showRequiredText
                label='Middle Name'
                name='middleName'
                dataTestId='middleName'
                onChange={(e) => {
                  setSibling({ ...sibling, middleName: e.target.value })
                }}
                value={sibling.middleName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldWithLabel
                required={lastNameError}
                showRequiredText={lastNameError}
                error={lastNameError}
                label='Last Name'
                name='lastName'
                dataTestId='lastName'
                onChange={(e) => {
                  setSibling({ ...sibling, lastName: e.target.value })
                  !isDirty.lastName && setIsDirty({ ...isDirty, lastName: true })
                }}
                value={sibling.lastName}
              />
            </Grid>
            {!sibling.isUnborn && <Grid item xs={12} sm={6}>
              <DatePickerWithLabel
                required={dateOfBirthError}
                showRequiredText={dateOfBirthError}
                error={dateOfBirthError}
                label='Date of Birth'
                dataTestId='dateOfBirth'
                value={sibling.dateOfBirth}
                onChange={(newValue) => {
                  setSibling({ ...sibling, dateOfBirth: newValue })
                  !isDirty.dateOfBirth && setIsDirty({ ...isDirty, dateOfBirth: true })
                }}
                name={'dateOfBirth'}
              />
            </Grid>
            }
            {sibling.isUnborn &&

              <Grid item xs={6}>
                <DatePickerWithLabel
                  required={dueDateError}
                  showRequiredText={dueDateError}
                  error={dueDateError}
                  label='Due Date'
                  onChange={(newValue) => {
                    setSibling({ ...sibling, dueDate: newValue })
                    !isDirty.dueDate && setIsDirty({ ...isDirty, dueDate: true })
                  }}
                  value={sibling.dueDate ?? null}
                  name='dueDate'
                />
              </Grid>
            }
            {!sibling.isUnborn && <Grid item xs={12} sm={6}>
              <SelectWithLabel
                required={birthGenderError}
                showRequiredText={birthGenderError}
                error={birthGenderError}
                width='100%'
                label='Assigned Gender at Birth'
                name='assignedGenderAtBirth'
                onChange={(e) => {
                  e.target.value !== null && setSibling({ ...sibling, birthGender: e.target.value })
                  !isDirty.birthGender && setIsDirty({ ...isDirty, birthGender: true })
                }}
                value={sibling.birthGender ?? ''}
                options={genderOptions}
              />
            </Grid>
            }
            <Grid item xs={12} sm={6}>
              <Stack sx={{ display: 'flex', height: '100%' }} direction='row' alignItems='center' flexWrap='wrap'>
                <Stack direction='row' alignItems='center' sx={{ paddingRight: 4 }}>
                  <FormControlLabel
                    label={<Typography variant='body1'>Informational Only</Typography>}
                    control={<Checkbox checked={sibling.informationalOnly}
                      onChange={(e) => {
                        setSibling({ ...sibling, informationalOnly: e.target.checked })
                      }}
                    />}
                  />
                  <LabelWithToolTip
                    toolTipText='Selecting this option means that this child will not be enrolling in MiECC services.  It will filter the child out of the main results on the Find Children feature'
                  />
                </Stack>
                <FormControlLabel
                  label={<Typography variant='body1'>Lives With {sibling.childFullName}</Typography>}
                  control={<Checkbox checked={sibling.livesWithChild}
                    onChange={(e) => {
                      setSibling({ ...sibling, livesWithChild: e.target.checked })
                    }}
                  />}
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }} gutterBottom>Relationships</Typography>
              <Stack direction='row' alignItems='center'>
                <FormControlLabel
                  label={<Typography variant='body1'>Shares All Members</Typography>}
                  control={<Checkbox name='sharesMembersCheckbox' checked={sibling.relationships.every(r => r.isSharedMember)}
                    onChange={(e) => {
                      const newRelationships = [...sibling.relationships].map((r) => {
                        return { ...r, isSharedMember: e.target.checked }
                      })

                      setSibling({ ...sibling, relationships: newRelationships })
                    }}
                  />}
                />
                <LabelWithToolTip
                  toolTipText='Selecting this item will select all other family members.  It will imply the relationship to each other member of the family but will need to be confirmed'
                />
              </Stack>
              <FormGroup sx={{ paddingLeft: 2 }}>
                <FormControlLabel
                  label={<Typography variant='body1'>{sibling.childFullName}</Typography>}
                  control={<Checkbox color='default' checked={true} />}
                />
                {[...sibling.relationships].map(r => (
                  <Grid container
                    direction='row'
                    justifyContent='start'
                    alignItems='center'
                    key={r.caregiverId}
                    width={{ xs: '100%', lg: '75%' }}
                  >
                    <Grid item >
                      <FormControlLabel
                        label={<Typography variant='body1'>{r.fullName}</Typography>}
                        control={<Checkbox data-testid={`${r.fullName}Checkbox`} checked={r.isSharedMember} onChange={(e) => {
                          const newRelationships = [...sibling.relationships].map((rel) => {
                            if (rel.caregiverId === r.caregiverId) {
                              return { ...rel, isSharedMember: e.target.checked }
                            }
                            return rel
                          })
                          setSibling({ ...sibling, relationships: newRelationships })
                        }}
                        />}
                      />
                    </Grid>
                    <Grid item sx={{ display: 'contents' }}>
                      {r.isSharedMember && <><Box sx={{ display: 'inline-block' }}>
                        <Select
                          fullWidth
                          size='small'
                          value={r.relationshipId}
                          onChange={(e) => {
                            const newRelationships = [...sibling.relationships].map((rel) => {
                              if (rel.caregiverId === r.caregiverId) {
                                return { ...rel, relationshipId: Number(e.target.value) }
                              }
                              return rel
                            })
                            setSibling({ ...sibling, relationships: newRelationships })
                          }}
                          sx={{
                            height: '30px', width: '200px'
                          }}
                        >
                          {[...relationships].map(rel => (
                            <MenuItem key={rel.id} value={rel.id}>
                              {rel.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                      </>}
                    </Grid>
                    <Grid item >
                      {r.isSharedMember && <CheckBoxWithLabel
                        fullWidth
                        dataTestId='livesWith'
                        value={r.livesWith}
                        setValue={(e) => {
                          const newRelationships = [...sibling.relationships].map((rel) => {
                            if (rel.caregiverId === r.caregiverId) {
                              return { ...rel, livesWith: e }
                            }
                            return rel
                          })
                          setSibling({ ...sibling, relationships: newRelationships })
                        } } name={'livesWith'} label='Lives With' edge={'end'}
                      />
                      }
                    </Grid>
                    <Grid item >
                      {r.isSharedMember && <CheckBoxWithLabel
                        fullWidth
                        value={r.financiallyResponsible}
                        setValue={(e) => {
                          const newRelationships = [...sibling.relationships].map((rel) => {
                            if (rel.caregiverId === r.caregiverId) {
                              return { ...rel, financiallyResponsible: e }
                            }
                            return rel
                          })
                          setSibling({ ...sibling, relationships: newRelationships })
                        } } name={''} label='Financially Responsible For' edge={'end'}
                      />
}
                    </Grid>
                  </Grid>
                ))}
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <RaceList
                raceList={sibling.childRaceList}
                setRaceList={(raceList) => {
                  setSibling({ ...sibling, childRaceList: raceList })
                }}
                setTribeStatus={(checked: boolean, value: number | null) => { setSibling({ ...sibling, tribalAffiliationStatus: checked, tribalAffiliationID: value }) }}
                tribalAffiliationID={sibling.tribalAffiliationID}
                tribalAffiliationStatus={sibling.tribalAffiliationStatus}

              />
            </Grid>
            <Grid item xs={12}>
              <EligiblityFactorList
                captureIEP={false}
                captureIFSP={false}
                captureEOIS={false}
                hasIEP={false}
                hasIFSP={false}
                hasEOIS={false}
                iepRecommendsInclusivePlacement={false}
                eligiblityList={sibling.eligibilityFactorList ?? []}
                enviromentalRiskList={sibling.environmentalRiskList ?? []}
                setEligibilityValues={(factors, envRisks, values) => {
                  setSibling({ ...sibling, eligibilityFactorList: factors, environmentalRiskList: envRisks, otherEligibilityFactor: values.otherFactor })
                }}
                otherFactor={sibling.otherEligibilityFactor}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography className="info-box-header">Additional Values</Typography>
              <Grid container>
                <Grid item sm={6}>
                  <CheckBoxWithLabel label='SNAP Benefits' name='hasSNAPBenefits' edge='start' value={sibling.hasSNAPBenefits} setValue={(val) => { valueChanged('hasSNAPBenefits', val) }} />
                </Grid>
                <Grid item sm={6}>
                  <CheckBoxWithLabel label='Foster Child' name='isFosterChild' edge='start' value={sibling.isFosterChild} setValue={(val) => { valueChanged('isFosterChild', val) }} />
                </Grid>
                <Grid item sm={6}>
                  <CheckBoxWithLabel label='Cash Assistance' name='isOnCashAssistance' edge='start' value={sibling.isOnCashAssistance} setValue={(val) => { valueChanged('isOnCashAssistance', val) }} />
                </Grid>
                <Grid item sm={6}>
                  <CheckBoxWithLabel label='Immigrant' name='isImmigrant' edge='start' value={sibling.isImmigrant} setValue={(val) => { valueChanged('isImmigrant', val) }} />
                </Grid>
                <Grid item sm={12}>
                  <CheckBoxWithLabel label='SSI (Supplemental Security Income)' name='isOnSSI' edge='start' value={sibling.isOnSSI} setValue={(val) => { valueChanged('isOnSSI', val) }} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', paddingTop: 4 }}>
            <Button
              variant='outlined'
              color='inherit'
              onClick={() => {
                nav(-1)
              }}
              sx={{ textTransform: 'none', paddingX: 5, fontWeight: 'bold' }}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              color='primary'
              onClick={handleSave}
              sx={{ textTransform: 'none', paddingX: 5 }}
              data-testid='saveSibling'
            >
              Save Sibling
            </Button>
          </Box>
        </>
      }
    </Paper>
  </>
}
